import { GiftIcon, Loader2Icon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useRedeemCodeInput } from '@/hooks/billing/use-redeem-code-input.hook'
import { cn } from '@/lib/utils'

export interface BillingRedeemCodeCardProps {
  onRedeem: (code: string) => void
  isLoading?: boolean
  className?: string
}

interface RedeemCodeFormProps {
  code: string
  isValidCode: boolean
  isLoading: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSubmit: (e: React.FormEvent) => void
}

const RedeemCodeForm: React.FC<RedeemCodeFormProps> = ({
  code,
  isValidCode,
  isLoading,
  onChange,
  onSubmit,
}) => (
  <form onSubmit={onSubmit} className="billing-redeem-code-form flex flex-col gap-3">
    <Input
      type="text"
      placeholder="Enter code"
      value={code}
      onChange={onChange}
      className="billing-redeem-code-input font-mono uppercase"
      autoComplete="off"
      disabled={isLoading}
    />
    <Button
      type="submit"
      disabled={!isValidCode || isLoading}
      className="billing-redeem-code-button w-full"
    >
      {isLoading ? (
        <>
          <Loader2Icon className="mr-2 size-4 animate-spin" />
          Redeeming...
        </>
      ) : (
        'Redeem'
      )}
    </Button>
  </form>
)

const BillingRedeemCodeCardBase: React.FC<BillingRedeemCodeCardProps> = ({
  onRedeem,
  isLoading = false,
  className,
}): ReactNode => {
  const { code, isValidCode, handleCodeChange, resetCode } = useRedeemCodeInput()

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!isValidCode || isLoading) return
    onRedeem(code.trim())
    resetCode()
  }

  return (
    <Card className={cn('billing-redeem-code-card h-full', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <GiftIcon className="size-4" />
          Redeem Code
        </CardTitle>
        <CardDescription>Enter a gift or promo code</CardDescription>
      </CardHeader>
      <CardContent>
        <RedeemCodeForm
          code={code}
          isValidCode={isValidCode}
          isLoading={isLoading}
          onChange={handleCodeChange}
          onSubmit={handleSubmit}
        />
      </CardContent>
    </Card>
  )
}

export const BillingRedeemCodeCard = memo(BillingRedeemCodeCardBase)

BillingRedeemCodeCard.displayName = 'BillingRedeemCodeCard'
